import Vue from "vue"
import _ from "lodash"
import Profile from "../modules/user/profile"
import Inventory from "./inventory/inventory.module"
import Coaching from "../modules/user/coaching"
import Payment from "./payment/payment.module"
import Subscriptions from "./subscriptions/subscriptions.module"

import {
  ackFeatureReleaseNotification,
  completeOnboarding,
  // fetchUser,
  fetchUserCapabilities,
  fetchUserSettings,
  updateUserUsername,
  updateUserEmail,
  updateUserPassword,
  updateUserAvatar,
  getUserLeagueOfLegendsInfo,
  updateUserLeagueOfLegendsInfo,
  getUserFortniteInfo,
  updateUserFortniteInfo,
  getUserHearthstoneInfo,
  updateUserHearthstoneInfo,
  getUserCSGOInfo,
  updateUserCSGOInfo,
  getUserActiveGames,
  checkFeatureReleaseNotification,
  getOnboardingStatus,
  updateUserDetails,
  getUserNameShouldBeUpdated,
  setUserGameStatus,
  setUserActiveGame,
  getUserReferralCode,
  sendUserInvite,
} from "../../api/user"
import {
  fetchUser,
  fetchUserGameProfile,
  updateUserGameProfile,
  updateCommunicationMethods,
} from "./user.apis"
import { syncServerLoginState } from "@/store/auth/auth.apis"

const state = () => ({
  user: undefined,
  userSettings: {},
  userCapabilities: {},
  userGameProfiles: undefined,
  leagueOfLegendsInfo: null,
  hearthstoneInfo: null,
  csgoInfo: null,
  fortniteInfo: null,
  activeGames: {},
  currentActiveGame: null,
  currentInstaproGameId: null,
  featureReleaseNotification: null,
  onboardingStatus: null,
  userNameShouldBeUpdated: false,
  referralCode: null,
  referralData: {},
  trialCancelStep: "cancel",
  communicationMethodsError: "",
})

const getters = {
  onboardingStatus: state => state.onboardingStatus,
  currentInstaproGameId: state => state.currentInstaproGameId,
  getUser: state => {
    return state.user
  },
  getUserGameProfile: state => gameId => (state.userGameProfiles || {})[gameId],
  featureReleaseNotification: state => state.featureReleaseNotification,
  getUserCapabilities: state => {
    return state.userCapabilities
  },
  isUserContentEditor: state => {
    return _.findKey(state.userCapabilities.administration || {}, gameCapabilities => {
      const contentCapabilities = gameCapabilities.content || []
      return _.intersection(contentCapabilities, ["create", "update", "delete"]).length > 0
    }) !== undefined
  },
  isUserPro: ({ userCapabilities }) => {
    if (!userCapabilities) {
      return false
    }
    if (Object.keys(userCapabilities).length === 0) {
      return false
    }
    for (let content of Object.values(userCapabilities.pro_content)) {
      if (content[0] === "view") {
        return true
      }
    }
    return false
  },
  activeGames: state => state.activeGames,
  currentActiveGame: state => state.currentActiveGame,
  userNameShouldBeUpdated: state => state.userNameShouldBeUpdated,
  coachServiceDiscount: state => {
    return state.user?.properties?.discounts?.pg_points?.amount ?? 0
  },
  referralCode: state => state.referralCode,
  referralData: state => state.referralData,
  isActiveGameFPS: (state, getters, rootState, rootGetters) =>
    rootGetters["Game/isGameFPS"](state.currentActiveGame),
  communicationMethodsError: state => state.communicationMethodsError,
  /** @deprecated Use User/Subscriptions/membershipSubscriptions */
  membership: (state, getters, rootState, rootGetters) => (rootGetters["User/Subscriptions/membershipSubscriptions"] ?? []),
  /** @deprecated Use User/Subscriptions/activeCoachSubscriptions and User/Subscriptions/expiredCoachSubscriptions */
  coachSubscription: (state, getters, rootState, rootGetters) => ({
    active: rootGetters["User/Subscriptions/activeCoachSubscriptions"] ?? [],
    expired: rootGetters["User/Subscriptions/expiredCoachSubscriptions"] ?? [],
  }),
}

const mutations = {
  SET_USER: (state, user) => {
    Vue.set(state, "user", user)
  },
  SET_USER_SETTINGS: (state, userSettings) => {
    Vue.set(state, "userSettings", userSettings)
  },
  ADD_USER_PROP: (state, payload) => {
    Vue.set(state.user, payload.prop, payload.value)
  },
  SET_USER_CAPABILITIES: (state, payload) => {
    Vue.set(state, "userCapabilities", payload)
  },
  SET_USER_GAME_PROFILE: (state, { gameId, userGameProfile }) => {
    state.userGameProfiles = state.userGameProfiles || {}
    Vue.set(state.userGameProfiles, gameId, userGameProfile)
  },
  SET_USER_LEAGUE_OF_LEGENDS_INFO: (state, leagueOfLegendsInfo) => {
    Vue.set(state, "leagueOfLegendsInfo", leagueOfLegendsInfo)
  },
  SET_USER_HEARTHSTONE_INFO: (state, hearthstoneInfo) => {
    Vue.set(state, "hearthstoneInfo", hearthstoneInfo)
  },
  SET_USER_FORTNITE_INFO: (state, fortniteInfo) => {
    Vue.set(state, "fortniteInfo", fortniteInfo)
  },
  SET_USER_CSGO_INFO: (state, csgoInfo) => {
    Vue.set(state, "csgoInfo", csgoInfo)
  },
  SET_USER_ACTIVE_GAMES: (state, activeGames) => {
    Vue.set(state, "activeGames", activeGames)
  },
  SET_USERNAME_SHOULD_BE_UPDATED: (state, userNameShouldBeUpdated) => {
    Vue.set(state, "userNameShouldBeUpdated", userNameShouldBeUpdated)
  },
  setCurrentActiveGame: (state, activeGame) =>
    (state.currentActiveGame = activeGame),
  setCurrentInstaproGameId: (state, id) => (state.currentInstaproGameId = id),
  setFeatureReleaseNotification: (state, featureReleaseNotification) =>
    (state.featureReleaseNotification = featureReleaseNotification),
  setOnboardingStatus: (state, onboardingStatus) =>
    (state.onboardingStatus = onboardingStatus),
  setReferralCode: (state, referralCode) => (state.referralCode = referralCode),
  setReferralData: (state, referralData) =>
    (state.referralData = { ...referralData }),
  setCommunicationMethodsError: (state, communicationMethodsError) =>
    (state.communicationMethodsError = communicationMethodsError),
}

const actions = {
  async getOnboardingStatus ({ commit }) {
    const data = await getOnboardingStatus()
    await commit("setOnboardingStatus", !data.is_completed)
  },
  async completeOnboarding ({ commit }) {
    await completeOnboarding()
    await commit("setOnboardingStatus", false)
  },
  async checkFeatureReleaseNotification ({ commit }, feature) {
    const data = await checkFeatureReleaseNotification(feature)
    await commit("setFeatureReleaseNotification", data.should_notify)
  },
  async ackFeatureReleaseNotification ({ commit }, feature) {
    commit("setFeatureReleaseNotification", false)
    await ackFeatureReleaseNotification(feature)
  },
  async loadUser ({ commit, state }) {
    // Check if user has already been fetched
    if (state.user) {
      return state.user
    }
    try {
      const userData = await fetchUser()
      if (userData !== null) {
        await syncServerLoginState()
      }
      commit("SET_USER", userData)
      return userData
    } catch (e) {
      commit("SET_USER", null)
    }
  },
  async checkUserNameShouldBeUpdated ({ commit }) {
    const userNameData = await getUserNameShouldBeUpdated()
    const userNameShouldBeUpdated = _.get(userNameData, "should_update", false)
    commit("SET_USERNAME_SHOULD_BE_UPDATED", userNameShouldBeUpdated)
  },
  loadUserSettings ({ commit }) {
    return fetchUserSettings().then(settings => {
      commit("SET_USER_SETTINGS", settings)
      return settings
    })
  },
  setUser ({ commit }, payload) {
    commit("SET_USER", payload)
  },
  addUserProp ({ commit }, payload) {
    commit("ADD_USER_PROP", payload)
  },
  async loadUserCapabilities ({ commit, dispatch, state }) {
    if (!_.isEmpty(state.userCapabilities)) {
      return
    }

    await dispatch("loadUser")

    if (state.user) {
      const capabilities = await fetchUserCapabilities()
      commit("SET_USER_CAPABILITIES", capabilities)
    }
  },
  setUserCapabilities ({ commit }, payload) {
    commit("SET_USER_CAPABILITIES", payload)
  },
  updateUserUsername ({ commit, state }, username) {
    return updateUserUsername(username).then(() =>
      commit("SET_USER", { ...state.user, username })
    )
  },
  updateUserDetails ({ commit, state }, { username, avatar, timezone }) {
    return updateUserDetails({ username, avatar, timezone }).then(() => {
      commit("SET_USER", { ...state.user, username, timezone })
      commit("SET_USER_SETTINGS", { ...state.userSettings, avatar })
    })
  },
  updateUserEmail ({ commit, state }, { currentPassword, email }) {
    return updateUserEmail({ currentPassword, email }).then(() =>
      commit("SET_USER", { ...state.user, email })
    )
  },
  updateUserPassword (
    { commit, state },
    { currentPassword, newPassword, confirmPassword }
  ) {
    return updateUserPassword({
      currentPassword,
      newPassword,
      confirmPassword,
    })
  },
  updateUserAvatar ({ commit, state }, avatar) {
    return updateUserAvatar(avatar).then(() =>
      commit("SET_USER_SETTINGS", { ...state.userSettings, avatar })
    )
  },
  async fetchUserGameProfile ({ commit }, gameId) {
    const userGameProfile = await fetchUserGameProfile(gameId)
    if (userGameProfile) {
      commit("SET_USER_GAME_PROFILE", { gameId, userGameProfile })
    }
  },
  async loadUserGameProfile ({ getters, dispatch }, gameId) {
    if (getters.getUserGameProfile(gameId) !== undefined) {
      return
    }
    await dispatch("fetchUserGameProfile", gameId)
  },
  async updateUserGameProfile ({ dispatch }, { gameId, profileData }) {
    const res = await updateUserGameProfile(gameId, profileData)
    if (!res.success) {
      throw new Error(res.message)
    }
    await dispatch("fetchUserGameProfile", gameId)
  },
  loadUserLeagueOfLegendsInfo ({ commit }) {
    return getUserLeagueOfLegendsInfo().then(userLeagueOfLegendsInfo => {
      commit("SET_USER_LEAGUE_OF_LEGENDS_INFO", userLeagueOfLegendsInfo)
    })
  },
  updateUserLeagueOfLegendsInfo ({ commit }, info) {
    return updateUserLeagueOfLegendsInfo(info).then(() => {
      const restructured = {
        summoner_name: info.summonerName,
        summoner_server: info.summonerServer,
        favorite_champion_ids: info.favoriteChampionIds,
        favorite_roles: info.favoriteRoles,
      }
      commit("SET_USER_LEAGUE_OF_LEGENDS_INFO", restructured)
    })
  },
  async loadUserFortniteInfo ({ commit }) {
    const userFortniteInfo = await getUserFortniteInfo()
    commit("SET_USER_FORTNITE_INFO", userFortniteInfo)
    return userFortniteInfo
  },
  updateUserFortniteInfo ({ commit }, info) {
    return updateUserFortniteInfo(info).then(() =>
      commit("SET_USER_FORTNITE_INFO", { epic_username: info.epicUsername })
    )
  },
  async loadUserHearthstoneInfo ({ commit }) {
    const userHearthstoneInfo = await getUserHearthstoneInfo()
    commit("SET_USER_HEARTHSTONE_INFO", userHearthstoneInfo)
    return userHearthstoneInfo
  },
  updateUserHearthstoneInfo ({ commit }, info) {
    return updateUserHearthstoneInfo(info).then(() =>
      commit("SET_USER_HEARTHSTONE_INFO", {
        supplied_rank: info.hearthstoneRank,
        preferred_archetype: info.preferredArchetype,
      })
    )
  },
  async loadUserCSGOInfo ({ commit }) {
    const userCSGOInfo = await getUserCSGOInfo()
    commit("SET_USER_CSGO_INFO", userCSGOInfo)
    return userCSGOInfo
  },
  updateUserCSGOInfo ({ commit }, info) {
    return updateUserCSGOInfo(info).then(() =>
      commit("SET_USER_CSGO_INFO", { csgo_rank: info.csgoRank })
    )
  },
  async loadUserAllGamesInfo ({ dispatch }) {
    dispatch("loadUserLeagueOfLegendsInfo")
    dispatch("loadUserFortniteInfo")
    dispatch("loadUserHearthstoneInfo")
    dispatch("loadUserCSGOInfo")
  },
  async loadUserActiveGames ({ commit }, forceReload) {
    forceReload = forceReload || false
    if (!_.isEmpty(state.activeGames) && !forceReload) {
      return state.activeGames
    }

    let activeGames = await getUserActiveGames()
    const games = activeGames?.games || []
    const keys = Object.keys(games)
    let currentGame = games[keys[0]]
    let currentKey = keys[0]
    for (let key of keys) {
      if (games[key].last_updated > currentGame.last_updated) {
        currentGame = games[key]
        currentKey = key
      }
    }
    commit("setCurrentActiveGame", currentKey)
    commit("SET_USER_ACTIVE_GAMES", games)
    return activeGames
  },
  async setUserGameStatus ({ dispatch }, body) {
    await setUserGameStatus(body)
    dispatch("loadUserActiveGames", true)
  },
  async setUserActiveGame ({ dispatch }, game) {
    await setUserActiveGame(game)
    dispatch("loadUserActiveGames", true)
  },
  async getCurrentInstaproGameId ({ dispatch, rootGetters, state, commit }) {
    await dispatch("Instapro/loadInstaproGames", null, { root: true })
    const instaproGames = rootGetters["Instapro/instaproGames"]
    for (let game of instaproGames) {
      if (game && game.name === state.currentActiveGame) {
        commit("setCurrentInstaproGameId", game.id)
      }
    }
  },
  async getUserReferralCode ({ commit }) {
    const data = await getUserReferralCode()
    commit("setReferralCode", data.code)
    commit("setReferralData", data.data)
  },
  async sendUserInvite (context, email) {
    const data = await sendUserInvite(email)
    return data
  },
  async updateCommunicationMethods ({ commit, state }, body) {
    const data = await updateCommunicationMethods(body)
    const user = state.user
    if (!data.success) {
      commit("setCommunicationMethodsError", data.message)
    } else {
      commit("SET_USER", user)
      if (body?.discord_id) {
        user.discord_id = body.discord_id
      }
      if (body?.twitch_id) {
        user.twitch_id = body.twitch_id
      }
    }
  },
  /** @deprecated Use User/Subscriptions/loadUserSubscriptions */
  async getUserSubscriptions ({ dispatch }) {
    await dispatch("User/Subscriptions/loadUserSubscriptions", undefined, { root: true })
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
  modules: {
    Profile,
    Inventory,
    Coaching,
    Payment,
    Subscriptions,
  },
}



// WEBPACK FOOTER //
// ./src/store/user/user.module.js